import type React from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { GenericFormType } from './types'

/**
 * Represents the shape of the form context.
 * @template T The type of the form data.
 */
interface FormContextType<T> {
  /** A record of form instances, keyed by their IDs. */
  forms: Record<string, GenericFormType<T>>
  /** Adds a new form instance to the context. */
  addForm: (id: string, instance: GenericFormType<T>) => void
  /** Removes a form instance from the context. */
  removeForm: (id: string) => void
}

/** The context for managing form instances. */
export const FormContext = createContext<FormContextType<unknown> | undefined>(undefined)

/**
 * Provides a context for managing multiple form instances.
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components.
 */
export const FormProvider = ({ children }: { children: React.ReactNode }) => {
  const [forms, setForms] = useState<Record<string, GenericFormType<unknown>>>({})

  const addForm = useCallback((id: string, instance: GenericFormType<unknown>) => {
    setForms((prev) => ({ ...prev, [id]: instance }))
  }, [])

  const removeForm = useCallback((id: string) => {
    setForms((prev) => {
      const { [id]: _, ...rest } = prev
      return rest
    })
  }, [])

  const value = useMemo(() => ({ forms, addForm, removeForm }), [forms, addForm, removeForm])

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>
}

/**
 * Hook to access a specific form instance from the FormContext.
 * @template T The type of the form data.
 * @param {string} id - The ID of the form to retrieve.
 * @returns {GenericFormType<T> | null} The form instance or null if not found.
 * @throws {Error} If used outside of a FormProvider.
 */
export const useFormInstance = <T,>(id: string): GenericFormType<T> | null => {
  const context = useContext(FormContext)
  if (!context) {
    throw new Error('useFormInstance must be used within a FormProvider')
  }
  if (!context.forms[id]) return null

  return context.forms[id] as GenericFormType<T>
}

export const useFormData = <T,>(id: string): T | null => {
  const form = useFormInstance<T>(id)
  const [data, setData] = useState<T | null>(null)

  useEffect(() => {
    if (!form) return
    setData(form.store.state.values as T)
    const unsubscribe = form.store.subscribe(() => {
      setData(form.store.state.values as T)
    })
    return () => unsubscribe()
  }, [form])

  return data
}

export function useSelectiveFormData<T, R>(id: string, selector: (state: T) => R): R | undefined {
  const form = useFormInstance<T>(id)
  const selectorRef = useRef(selector)
  selectorRef.current = selector
  const [data, setData] = useState<R>()

  useEffect(() => {
    if (!form) return
    const updateData = () => {
      const d = selectorRef.current(form.store.state.values as T)
      setData(d)
    }
    updateData()
    const unsubscribe = form.store.subscribe(updateData)
    return () => unsubscribe()
  }, [form])

  return data
}
